import type { MapFeatureEdit } from "@domain/CRM/job";
import { apiClient } from "../../../client";
import { StandardAxiosConfig, getBaseDataURL } from "../../../common";

/**
 * The scheduling board's four batch partial-writes on Jobs, mirroring the backend sub-router
 * that serves them (`API/src/routers/data/jobs_scheduling.py`). Each maps the domain's
 * `{ jobId, … }` shape onto the wire's `{ id, … }` and POSTs to a dedicated route — a full-Job
 * partial can't go through `/update`, because Job's many required fields would fail validation
 * for an id-plus-one-field payload. `null` clears the field; empty input is a no-op that never
 * touches the network.
 */
async function postBatch(route: string, body: unknown[], failureLabel: string): Promise<void> {
	if (body.length === 0) return;
	let status: number;
	try {
		status = (await apiClient.post(`${getBaseDataURL("jobs")}/${route}`, body, StandardAxiosConfig())).status;
	} catch (err) {
		const detail = serverDetail(err);
		throw new Error(`Failed to update job ${failureLabel}${detail ? `: ${detail}` : ""}`);
	}
	if (status !== 200) {
		throw new Error(`Failed to update job ${failureLabel}`);
	}
}

/** Why the server refused, in its own words — `undefined` when it did not say. These routes
 *  answer a rejected save with a 422 whose `detail` names the offending job id (a duplicate id
 *  in the batch, map features too large for the column); axios' own message ("Request failed
 *  with status code 422") drops it, leaving the board with nothing to act on. FastAPI's
 *  automatic validation 422s carry a list of `{ msg, loc }` instead of a string, so handle both. */
function serverDetail(err: unknown): string | undefined {
	const detail = (err as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail;
	if (typeof detail === "string") return detail || undefined;
	if (Array.isArray(detail) && detail.length > 0) {
		return detail.map((item) => (typeof item?.msg === "string" ? item.msg : JSON.stringify(item))).join("; ");
	}
	return undefined;
}

/** `Jobs.Order` — the scheduling run-sequence. `order: null` clears it (job unassigned). */
export function postJobOrders(updates: { jobId: string; order: number | null }[]): Promise<void> {
	return postBatch(
		"update-orders",
		updates.map((u) => ({ id: u.jobId, order: u.order })),
		"orders",
	);
}

/** `Jobs."Dropoffs/Shipping"` — which shift-dropoff stop receives each job's samples. */
export function postJobDropoffs(updates: { jobId: string; shiftDropoffId: string | null }[]): Promise<void> {
	return postBatch(
		"update-dropoffs",
		updates.map((u) => ({ id: u.jobId, shiftDropoffId: u.shiftDropoffId })),
		"dropoffs",
	);
}

/** `Jobs.Location` — the cluster pull-in point. Provenance (`Lat Lon Source`) is stamped by the
 *  endpoint, never sent by the caller. */
export function postJobLocations(updates: { jobId: string; location: string | null }[]): Promise<void> {
	return postBatch(
		"update-locations",
		updates.map((u) => ({ id: u.jobId, location: u.location })),
		"locations",
	);
}

/** `Jobs."Map Features GeoJSON (code)"` — pull-in zone DELTAS (`add` features, `clear` kinds), merged by
 *  the endpoint into each job's current text. */
export function postJobMapFeatureEdits(edits: MapFeatureEdit[]): Promise<void> {
	return postBatch(
		"update-map-features",
		edits.map((e) => ({ id: e.jobId, add: e.add, clear: e.clear })),
		"map features",
	);
}
